import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CampaignResponse,
  CreateCampaignParams,
  UseCaseOption,
  UseCasesResponse,
  useCreateCampaignApi,
  useGetCampaignApi,
  useGetCampaignFlyerApi,
  useGetUseCasesApi,
} from 'src/api/campaignRegistry';
import { handleApiError, focusFieldByName } from 'src/api/campaignRegistry/apiErrorParser';
import { useGetUsersApi } from 'src/api/users';
import { DefaultCustomField } from 'src/constants/types';
import { useSaveCompanyCampaignInfoApi } from '../api';
import { useFormErrors, useFormState } from '../components/EditCompanyView/shared/hooks';
import {
  COMPANY_PHONE_NUMBER_PLACEHOLDER,
  MAX_SAMPLE_MESSAGES,
  buildDefaultCampaignInfoPayload,
  buildHelpMessage,
  getExistingSampleMessages,
  getHelpMessage,
} from '../helpers';
import {
  AdminCompany,
  CampaignFormState,
  CampaignInfoTabProps,
  CampaignInfoTabViewModel,
  CampaignKeywordType,
} from '../types';

export const useCampaignInfoTabController = (
  params: CampaignInfoTabProps,
): CampaignInfoTabViewModel => {
  const { company, refreshCompany } = params;

  const createCampaign = useCreateCampaignApi();
  const getUseCases = useGetUseCasesApi();
  const getCampaign = useGetCampaignApi();
  const getCampaignFlyer = useGetCampaignFlyerApi();
  const saveCompanyCampaignInfo = useSaveCompanyCampaignInfoApi();
  const getUsers = useGetUsersApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [useCasesData, setUseCasesData] = useState<UseCasesResponse>({});
  const [existingCampaign, setExistingCampaign] = useState<CampaignResponse | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState<boolean>(false);
  const loadedCompanyIdRef = useRef<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [contactEmail, setContactEmail] = useState<string | undefined>(undefined);

  const { fieldErrors, setFieldErrors, clearFieldError, clearAllErrors } = useFormErrors();

  const { formValues, updateField, updateFields } = useFormState<CampaignFormState>(
    {
      ...buildDefaultCampaignInfoPayload(
        company.name,
        company.phoneNumbers[0]?.number,
        undefined,
        company.useCase || '',
        company.subUseCases || [],
      ),
      // Form-state-only fields not part of the API payload shape
      companyId: company.id,
      campaignId: company.tcrCampaignId || undefined,
      status: undefined,
      brandId: company.brandId || '',
      // The form edits keywords as arrays; the API payload uses comma-joined strings
      helpKeywords: ['HELP'],
      optinKeywords: ['START'],
      optoutKeywords: ['STOP'],
      // Help message is left blank initially; resolved from contactEmail on save
      helpMessage: '',
    },
    clearFieldError,
  );

  const phoneError = useMemo(() => {
    return (formValues.messageFlow?.includes(COMPANY_PHONE_NUMBER_PLACEHOLDER))
      ? 'Company Phone is required. Update Message Flow manually or add a phone number to the company.'
      : undefined;
  }, [formValues.messageFlow]);

  const emailError = useMemo(() => {
    return (!contactEmail)
      ? 'Contact Email is required. Update Help Message manually or add a contact email to the company.'
      : undefined;
  }, [contactEmail]);

  // Define template variables as customFields
  // TemplateText component expects DefaultCustomField[] format
  const customFields: DefaultCustomField[] = useMemo(
    () => [
      {
        name: 'flyerLink',
        description: 'Flyer Link',
        type: 'text',
        hasDefaultValue: Boolean(formValues.flyerLink?.trim()),
      } as DefaultCustomField,
      {
        name: 'companyPhoneNumber',
        description: 'Company Phone Number',
        type: 'text',
        hasDefaultValue: Boolean(company.phoneNumbers[0]?.number?.trim()),
      } as DefaultCustomField,
      {
        name: 'contactEmail',
        description: 'Contact Email',
        type: 'text',
        hasDefaultValue: Boolean(contactEmail?.trim()),
      } as DefaultCustomField,
    ],
    [formValues.flyerLink, company.phoneNumbers, contactEmail],
  );

  const defaultHelpMessage = useMemo(
    () => buildHelpMessage(company.name, contactEmail),
    [company.name, contactEmail],
  );

  const [newOptinKeyword, setNewOptinKeyword] = useState<string | undefined>('');
  const [newOptoutKeyword, setNewOptoutKeyword] = useState<string | undefined>('');
  const [newHelpKeyword, setNewHelpKeyword] = useState<string | undefined>('');
  const [visibleSampleMessages, setVisibleSampleMessages] = useState<number[]>([1]);

  const isReadOnly = !!existingCampaign?.campaignId || !!company.tcrCampaignId;

  const populateFormFromCampaign = useCallback(
    (campaignData: CampaignResponse): Partial<CampaignFormState> => {
      const replaceUrl = (text: string | undefined): string | undefined => {
        if (!text || !campaignData.flyerLink) return text;
        // Escape special regex characters in the URL
        const escapedUrl = campaignData.flyerLink.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text.replace(new RegExp(escapedUrl, 'g'), '{{flyerLink}}');
      };
      return {
        campaignId: campaignData.campaignId,
        status: campaignData.status,
        brandId: campaignData.brandId,
        usecase: campaignData.usecase,
        subUsecases: Array.isArray(campaignData.subUsecases)
          ? campaignData.subUsecases
          : (campaignData.subUsecases as unknown as string)?.split(',').filter((s: string) => s.trim()) || [],
        resellerId: campaignData.resellerId || undefined,
        flyerLink: campaignData.flyerLink,
        description: replaceUrl(campaignData.description) || campaignData.description,
        messageFlow: replaceUrl(campaignData.messageFlow) || campaignData.messageFlow,
        sample1: campaignData.sample1,
        sample2: campaignData.sample2,
        sample3: campaignData.sample3,
        sample4: campaignData.sample4,
        sample5: campaignData.sample5,
        subscriberHelp: campaignData.subscriberHelp,
        subscriberOptin: campaignData.subscriberOptin,
        subscriberOptout: campaignData.subscriberOptout,
        helpMessage: campaignData.helpMessage,
        helpKeywords: campaignData.helpKeywords?.split(',') || [],
        optinKeywords: campaignData.optinKeywords?.split(',') || [],
        optoutKeywords: campaignData.optoutKeywords?.split(',') || [],
        optinMessage: campaignData.optinMessage,
        optoutMessage: campaignData.optoutMessage,
        privacyPolicyLink: replaceUrl(campaignData.privacyPolicyLink) || campaignData.privacyPolicyLink,
        termsAndConditionsLink: replaceUrl(campaignData.termsAndConditionsLink) || campaignData.termsAndConditionsLink,
        mock: campaignData.mock || false,
      };
    },
    [],
  );

  const populateFormFromCompanyCampaignInfo = (
    campaignInfo: NonNullable<AdminCompany['additionalData']>['campaign'],
  ): Partial<CampaignFormState> => {
    if (!campaignInfo) return {};
    return {
      usecase: campaignInfo.usecase,
      subUsecases: campaignInfo.subUsecases,
      resellerId: campaignInfo.resellerId,
      description: campaignInfo.description,
      messageFlow: campaignInfo.messageFlow,
      flyerLink: campaignInfo.flyerLink,
      privacyPolicyLink: campaignInfo.privacyPolicyLink,
      termsAndConditionsLink: campaignInfo.termsAndConditionsLink,
      sample1: campaignInfo.sample1,
      sample2: campaignInfo.sample2,
      sample3: campaignInfo.sample3,
      sample4: campaignInfo.sample4,
      sample5: campaignInfo.sample5,
      subscriberHelp: campaignInfo.subscriberHelp,
      subscriberOptin: campaignInfo.subscriberOptin,
      subscriberOptout: campaignInfo.subscriberOptout,
      helpMessage: campaignInfo.helpMessage,
      helpKeywords: campaignInfo.helpKeywords.split(','),
      optinKeywords: campaignInfo.optinKeywords.split(','),
      optoutKeywords: campaignInfo.optoutKeywords.split(','),
      optinMessage: campaignInfo.optinMessage,
      optoutMessage: campaignInfo.optoutMessage,
      mock: campaignInfo.mock || false,
    };
  };

  const getVisibleSampleMessages = useCallback(
    (campaignData: CampaignResponse): number[] => {
      // Convert CampaignResponse to CampaignFormState format for the utility function
      const mockFormValues: CampaignFormState = {
        ...campaignData,
        companyId: company.id,
        flyerLink: campaignData.flyerLink,
        campaignId: campaignData.campaignId,
        status: campaignData.status,
        subUsecases: campaignData.subUsecases || [],
        helpKeywords: campaignData.helpKeywords?.split(',') || [],
        optinKeywords: campaignData.optinKeywords?.split(',') || [],
        optoutKeywords: campaignData.optoutKeywords?.split(',') || [],
      };
      return getExistingSampleMessages(mockFormValues);
    },
    [company.id],
  );

  useEffect(() => {
    getUseCases()
      .then(useCasesResponse => {
        if (useCasesResponse.data && Object.keys(useCasesResponse.data).length > 0) {
          setUseCasesData(useCasesResponse.data);
        } else {
          setUseCasesData({});
        }

        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading use cases, sub-usecases:', error);
        toast.error('Failed to load form data');
        setUseCasesData({});
        setLoading(false);
      });
  }, [getUseCases]);

  // Reset campaign loaded state when company changes
  useEffect(() => {
    setExistingCampaign(null);
    loadedCompanyIdRef.current = null;
  }, [company.id]);

  useEffect(() => {
    if (!company.id || loadedCompanyIdRef.current === company.id) return;

    loadedCompanyIdRef.current = company.id;

    const savedCampaignInfo = company.additionalData?.campaign;
    if (savedCampaignInfo) {
      updateFields({
        ...populateFormFromCompanyCampaignInfo(savedCampaignInfo),
        brandId: company.brandId || '',
      });
    }

    setLoadingCampaign(true);
    getCampaign(company.id)
      .then(response => {
        const campaignData = response.data.campaign;
        const error = response.data.error;
        if (campaignData != null) {
          setExistingCampaign(campaignData);
          updateFields(populateFormFromCampaign(campaignData));
          setVisibleSampleMessages(getVisibleSampleMessages(campaignData));
        }
        if (error) {
          handleApiError(error, setFieldErrors, focusFieldByName);
        }
        return campaignData;
      })
      .then(campaignData => {
        if (campaignData?.flyerLink == null && company.id) {
          if (company.phoneNumbers.length > 0) {
            return getCampaignFlyer(company.id)
              .then(flyerResponse => {
                if (flyerResponse.data.url) {
                  if (campaignData) {
                    campaignData.flyerLink = flyerResponse.data.url;
                    updateFields(populateFormFromCampaign(campaignData));
                  } else {
                    updateField('flyerLink', flyerResponse.data.url);
                  }
                }
              })
              .catch(error => {
                setFieldErrors({ flyerLink: error.message });
              });
          } else {
            setFieldErrors({ flyerLink: 'No phone numbers found for this company. Add a phone number to the company.' });
          }
        }
      })
      .catch(error => {
        console.error('Error loading campaign:', error);
      })
      .finally(() => {
        setLoadingCampaign(false);
      });
  }, [
    company.id,
    company.brandId,
    company.phoneNumbers,
    company.additionalData?.campaign,
    getCampaign,
    updateFields,
    updateField,
    setFieldErrors,
    populateFormFromCampaign,
    populateFormFromCompanyCampaignInfo,
    getVisibleSampleMessages,
  ]);

  // Load company users
  useEffect(() => {
    if (!company.id) return;

    setLoadingUsers(true);
    getUsers({
      companyId: company.id,
      sort: 'role_asc',
      limit: 1,
      offset: 0,
    })
      .then(response => {
        const users = response.data.items;
        const user = users[0];

        if (!user?.email) {
          throw new Error('Company user email is required');
        }

        setContactEmail(user.email);
      })
      .catch(() => {
        setFieldErrors({ helpMessage: 'Failed to load company user email.' });
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, [company.id, getUsers, setFieldErrors]);

  const getUseCaseDetails = (useCaseKey: string): UseCaseOption | null => {
    if (useCasesData[useCaseKey]) {
      return useCasesData[useCaseKey];
    }

    return null;
  };

  const onInputChange = useCallback(
    (field: keyof CreateCampaignParams | 'flyerLink', value: string | string[] | boolean) => {
      updateField(field as keyof CampaignFormState, value as CampaignFormState[keyof CampaignFormState]);
    },
    [updateField],
  );

  const onAddKeyword = (
    keywordType: CampaignKeywordType,
    keywordValue: string,
    setKeywordValue: (value: string) => void,
  ) => {
    if (keywordValue.trim()) {
      const currentKeywords = (formValues[keywordType] as string[]) || [];
      if (!currentKeywords.includes(keywordValue.trim())) {
        onInputChange(keywordType, [...currentKeywords, keywordValue.trim()]);
      }
      setKeywordValue('');
    }
  };

  const onRemoveKeyword = (keywordType: CampaignKeywordType, keywordToRemove: string) => {
    const currentKeywords = (formValues[keywordType] as string[]) || [];
    onInputChange(
      keywordType,
      currentKeywords.filter(k => k !== keywordToRemove),
    );
  };

  const onAddSampleMessage = () => {
    if (visibleSampleMessages.length < MAX_SAMPLE_MESSAGES) {
      const nextSampleNumber = Math.max(...visibleSampleMessages) + 1;
      if (nextSampleNumber <= MAX_SAMPLE_MESSAGES) {
        setVisibleSampleMessages(prev => [...prev, nextSampleNumber]);
      }
    }
  };

  const onRegister = useCallback(() => {
    setSaving(true);
    clearAllErrors();

    const helpMessage = getHelpMessage(formValues.helpMessage, company.name, contactEmail);

    const campaignData = {
      ...formValues,
      campaignId: undefined,
      status: undefined,
      helpMessage,
      helpKeywords: formValues.helpKeywords.join(','),
      optinKeywords: formValues.optinKeywords.join(','),
      optoutKeywords: formValues.optoutKeywords.join(','),
    };

    createCampaign(campaignData)
      .then(response => {
        const campaignResponse = response.data;
        const campaignId = campaignResponse.campaignId;

        // Immediately update form state with the new campaignId
        updateFields({
          campaignId: campaignId,
          status: campaignResponse.status,
        });

        toast.success('Campaign registered successfully');
        refreshCompany();
      })
      .catch(error => {
        handleApiError(error, setFieldErrors, focusFieldByName);
        toast.error('Failed to register campaign');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    formValues,
    company.name,
    contactEmail,
    createCampaign,
    updateFields,
    refreshCompany,
    setFieldErrors,
  ]);

  const onSaveInfo = useCallback(() => {
    if (!company?.id) return;

    setSaving(true);
    setFieldErrors({});

    const helpMessage = getHelpMessage(formValues.helpMessage, company.name, contactEmail);

    const campaignInfoPayload = {
      usecase: formValues.usecase || '',
      subUsecases: formValues.subUsecases || [],
      resellerId: formValues.resellerId || 'R000000',
      description: formValues.description || '',
      messageFlow: formValues.messageFlow || '',
      flyerLink: formValues.flyerLink || '',
      privacyPolicyLink: formValues.privacyPolicyLink || '',
      termsAndConditionsLink: formValues.termsAndConditionsLink || '',
      sample1: formValues.sample1 || '',
      sample2: formValues.sample2 || undefined,
      sample3: formValues.sample3 || undefined,
      sample4: formValues.sample4 || undefined,
      sample5: formValues.sample5 || undefined,
      subscriberHelp: formValues.subscriberHelp || false,
      subscriberOptin: formValues.subscriberOptin || false,
      subscriberOptout: formValues.subscriberOptout || false,
      helpMessage,
      helpKeywords: (formValues.helpKeywords || []).join(','),
      optinKeywords: (formValues.optinKeywords || []).join(','),
      optoutKeywords: (formValues.optoutKeywords || []).join(','),
      optinMessage: formValues.optinMessage || '',
      optoutMessage: formValues.optoutMessage || '',
      mock: formValues.mock,
    };

    saveCompanyCampaignInfo(company.id, campaignInfoPayload)
      .then(() => {
        toast.success('Campaign info saved successfully');
        refreshCompany();
      })
      .catch(error => {
        console.error('Error saving campaign info:', error);
        handleApiError(error, setFieldErrors, focusFieldByName);
        toast.error('Failed to save campaign info');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [formValues, company?.id, company.name, contactEmail, saveCompanyCampaignInfo, refreshCompany, setFieldErrors]);

  const isFormValid = () => {
    const useCaseDetails = getUseCaseDetails(formValues.usecase);
    const minSubUsecases = useCaseDetails?.minSubUsecases || 0;

    // Check if sub-usecases requirement is met
    // Valid if minSubUsecases is 0 (no sub-usecases required) OR the required number of sub-usecases are selected
    const subUsecasesValid =
      minSubUsecases === 0 || (formValues.subUsecases || []).length >= minSubUsecases;

    // Check if at least one subscriber option is enabled
    const hasEnabledSubscriberOption =
      formValues.subscriberHelp || formValues.subscriberOptin || formValues.subscriberOptout;

    // Check if enabled groups have required fields
    const helpGroupValid =
      !formValues.subscriberHelp ||
      ((formValues.helpMessage || defaultHelpMessage) && !emailError && (formValues.helpKeywords || []).length > 0);

    const optinGroupValid =
      !formValues.subscriberOptin ||
      (formValues.optinMessage && (formValues.optinKeywords || []).length > 0);

    const optoutGroupValid =
      !formValues.subscriberOptout ||
      (formValues.optoutMessage && (formValues.optoutKeywords || []).length > 0);

    return !!(
      formValues.brandId &&
      formValues.usecase &&
      formValues.description &&
      formValues.messageFlow && phoneError === undefined &&
      formValues.flyerLink &&
      formValues.privacyPolicyLink &&
      formValues.termsAndConditionsLink &&
      formValues.sample1 &&
      hasEnabledSubscriberOption &&
      helpGroupValid &&
      optinGroupValid &&
      optoutGroupValid &&
      subUsecasesValid
    );
  };

  return {
    isLoading: loading || loadingCampaign || loadingUsers,
    saving,
    isReadOnly,
    companyTcrCampaignId: company.tcrCampaignId,
    existingCampaign,
    useCasesData,
    customFields,
    formValues,
    fieldErrors,
    phoneError,
    emailError,
    visibleSampleMessages,
    newOptinKeyword,
    newOptoutKeyword,
    newHelpKeyword,
    setNewOptinKeyword,
    setNewOptoutKeyword,
    setNewHelpKeyword,
    updateField,
    onInputChange,
    onAddKeyword,
    onRemoveKeyword,
    onAddSampleMessage,
    onRegister,
    onSaveInfo,
    isFormValid,
  };
};
